import { usePopup } from '@/contexts/PopupContext';
import { serviceCategories } from '@/data/services';
import { heroImages } from '@/data/images';
import SEO from '@/components/shared/SEO';
import PricingSection from '@/components/shared/PricingSection';
import TestimonialSlider from '@/components/shared/TestimonialSlider';
import CTABanner from '@/components/shared/CTABanner';
import LeadForm from '@/components/shared/LeadForm';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { ArrowRight, ShieldCheck, Clock, Users, Award, Globe, HeadphonesIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

const heroImg = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80&auto=format';
const infoImg = 'https://images.pexels.com/photos/7108454/pexels-photo-7108454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const whyUs = [
  { icon: ShieldCheck, title: 'Trusted Protection', desc: 'Expert-led trademark filing services ensuring the highest level of brand protection. Strong brand name trademark registration with full legal compliance. Reliable online trademark registration for complete security.' },
  { icon: Clock, title: 'Fast Processing', desc: 'Streamlined filing process for quick and efficient applications. Dedicated case managers help you register a trademark in USA faster. Fast-track support for smooth approval and processing.' },
  { icon: Users, title: 'Dedicated Support', desc: 'Personal case managers guiding you through every step of registration. Full assistance for trademark application services and documentation. Continuous support for stress-free filing experience.' },
  { icon: Award, title: 'Proven Success', desc: 'Thousands of successful trademark registration USA approvals with high accuracy. Trusted trademark registration company with proven results. Strong success rate in trademark protection services.' },
  { icon: Globe, title: 'Nationwide Coverage', desc: 'Federal protection securing your brand across all 50 states. Complete USA trademark registration for nationwide rights. Full legal coverage ensuring long-term brand safety.' },
  { icon: HeadphonesIcon, title: '24/7 Assistance', desc: 'Round-the-clock support to address your questions and concerns at any time.' },
];

const mediaLogos = ['Forbes', 'Bloomberg', 'Reuters', 'TechCrunch', 'Inc.', 'Entrepreneur'];

const faqs = [
  { q: 'Why do I need a trademark for my business identity?', a: 'A trademark protects your brand name, logo, and identity in the market. With trademark registration USA, you secure exclusive rights through USA trademark registration and ensure full legal protection of your brand.' },
  { q: 'How does the trademark registration process work in the USA?', a: 'The process of register a trademark in USA includes filing, examination, and approval. Our trademark filing services and trademark application services make it simple to file a trademark online with expert guidance.' },
  { q: 'Can I protect both my business name and logo?', a: 'Yes, you can protect both separately using online trademark registration. We assist with brand name trademark registration and also help you register company logo trademark for full protection.' },
  { q: 'Is a business license the same as trademark protection?', a: 'No, a business license does not give brand ownership rights. Only trademark registration USA provides legal protection through proper trademark protection services.' },
  { q: 'What support do I get after trademark approval?', a: 'After registration, you receive nationwide rights under USA trademark registration. We also provide trademark monitoring services to protect your brand from misuse and infringement.' },
];

export default function HomePage() {
  const { openPopup } = usePopup();

  return (
    <div data-testid="home-page">
      <SEO
        title="Trademark Registration & Brand Protection Services"
        description="Protect your brand with Trademark Pact's professional trademark registration services. Expert guidance through USPTO filing, comprehensive searches, and brand protection across all 50 states."
      />
      {/* 1. Hero Section */}
      <section className="relative pt-28 pb-12 lg:pt-28 lg:pb-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={heroImages.home} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#064E3B]/95 via-[#064E3B]/75 to-[#064E3B]/30" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wide border border-white/20">
                <ShieldCheck className="w-3.5 h-3.5" /> TRUSTED TRADEMARK SERVICES IN THE USA
              </div>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white leading-tight tracking-tight mb-6">
                Don't Let Anyone Steal<br />Your Brand Identity
              </h1>
              <p className="text-emerald-100 text-base lg:text-lg leading-relaxed mb-8">
                Federal trademark registration USA gives you exclusive nationwide rights. Our specialists at Trademark Pact handle USA trademark registration and help you register a trademark in USA with complete trademark filing services.
              </p>
              <div className="flex items-center gap-6 text-sm text-emerald-200">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-300" /> 98% Success Rate</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-300" /> Fast Processing</div>
                <div className="flex items-center gap-2"><HeadphonesIcon className="w-4 h-4 text-emerald-300" /> 24/7 Support</div>
              </div>
            </div>
            <div className="order-1 md:order-2 bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden md:mt-16 max-w-md mx-auto" data-testid="hero-form">
              <div className="bg-[#064E3B] px-6 py-5">
                <h2 className="font-heading font-bold text-lg text-white">Trademark Your Business Identity</h2>
                <p className="text-emerald-200 text-sm">Protect your brand with expert trademark registration.</p>
              </div>
              <div className="p-6">
                <LeadForm packageTitle="Essentials" packageAmount={45} embedded={true} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Informative Section */}
      <section className="py-12 lg:py-28 bg-white" data-testid="info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-[#10B981] font-semibold text-sm tracking-wider uppercase">Why Trademark Protection Matters</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mt-3 mb-6">
                One Registration. Nationwide Rights. Total Peace of Mind.
              </h2>
              <p className="text-slate-600 leading-relaxed mb-5">
                In today’s competitive marketplace, your brand identity is what sets you apart. Trademark registration USA provides the legal foundation to protect your name, logo, and reputation through secure USA trademark registration services.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Without trademark protection, anyone can use a similar name or logo, causing confusion and diluting your brand value. Our online trademark registration and trademark application services help you file a trademark online easily. 
              </p>
              <button
                onClick={() => openPopup()}
                className="bg-[#064E3B] text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-[#065F46] transition-colors flex items-center gap-2"
                data-testid="info-cta-btn"
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <img
                src={infoImg}
                alt="Professional trademark consultation"
                className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#064E3B] text-white p-5 rounded-xl shadow-lg hidden lg:block">
                <div className="font-heading font-extrabold text-2xl">10K+</div>
                <div className="text-emerald-200 text-sm">Trademarks Filed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

    

      {/* 4. Services Overview */}
      <section className="py-12 lg:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-4">
              Full-Spectrum Legal Solutions for Growing Businesses
            </h2>
            <p className="text-slate-600 text-base lg:text-lg max-w-2xl mx-auto">
              We provide trademark registration USA services for all business types. Our experts simplify USA trademark registration with full legal support and guidance. 
            </p>
          </div>
          {/* Desktop/Mobile Slider */}
          <div className="relative px-8">
            <Carousel opts={{ align: 'start', loop: true }}>
              <CarouselContent className="-ml-4">
                {serviceCategories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <CarouselItem key={cat.id} className="pl-4 basis-[100%] md:basis-[50%] lg:basis-[33.333%]">
                      <Link to={`/services/${cat.services[0].slug}`} className="block p-6 rounded-2xl border border-slate-100 bg-white h-100-im">
                        <div className="w-12 h-12 rounded-xl bg-[#064E3B] flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-heading font-bold text-lg text-[#064E3B] mb-2">{cat.name}</h3>
                        <p className="text-sm text-slate-600">{cat.description}</p>
                      </Link>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
              <CarouselNext className="border-emerald-200 text-[#064E3B]" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-4">
              The Trademark Pact Difference
            </h2>
            <p className="text-slate-600 text-base lg:text-lg max-w-2xl mx-auto">
             We combine legal expertise with dedicated service to deliver outstanding results for every client through trademark registration USA solutions. Our team ensures smooth USA trademark registration with complete guidance and support. 
            </p>
          </div>
          {/* Desktop/Mobile Slider */}
          <div className="relative px-8">
            <Carousel opts={{ align: 'start', loop: true }}>
              <CarouselContent className="-ml-4">
                {whyUs.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <CarouselItem key={i} className="pl-4 basis-[100%] md:basis-[50%] lg:basis-[33.333%]">
                      <div className="p-6 bg-white rounded-2xl border border-emerald-100 h-full">
                        <Icon className="w-8 h-8 text-[#10B981] mb-4" />
                        <h3 className="font-heading font-bold text-base text-[#064E3B] mb-2">{item.title}</h3>
                        <p className="text-sm text-slate-600">{item.desc}</p>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
              <CarouselNext className="border-emerald-200 text-[#064E3B]" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* 7. Pricing Section */}
      <PricingSection />

      {/* 8. Testimonials */}
      <TestimonialSlider />

      {/* 9. FAQ Section */}
      <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="bg-white rounded-xl border border-emerald-100 group" data-testid={`faq-item-${i}`}>
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-heading font-semibold text-sm text-[#064E3B] list-none">
                      {faq.q}
                      <span className="text-[#10B981] text-lg transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <div className="px-6 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                alt="FAQ support"
                className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 10. CTA Banner */}
      <CTABanner />
    </div>
  );
}
